import {GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {clsx} from 'clsx'

import {DiffCompactLinesPopover} from './components/DiffCompactLinesPopover'
import {
  DiffLinePresentationToggles,
  type DiffLinePresentationTogglesProps,
} from './components/DiffLinePresentationToggles'
import {DiffViewPreferenceToggle} from './components/DiffViewPreferenceToggle'
import {DiffCommentsPreferenceToggle} from './components/DiffCommentsPreferenceToggle'
import {useAnalytics} from '@github-ui/use-analytics'

export type DiffViewSettingsProps = {
  invisible?: boolean
  reloadOnSplitPreferenceChange?: boolean
  reloadOnWhitespaceChange?: boolean
  small?: boolean
} & Pick<
  DiffLinePresentationTogglesProps,
  'defaultHideWhitespace' | 'lineSpacingPreferenceAvailable' | 'onUpdateWhitespace'
>

export function DiffViewSettings({
  invisible = true,
  lineSpacingPreferenceAvailable = true,
  defaultHideWhitespace,
  onUpdateWhitespace,
  reloadOnSplitPreferenceChange = false,
  reloadOnWhitespaceChange = false,
  small,
}: DiffViewSettingsProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const handleDiffSettingsClick = () => {
    sendAnalyticsEvent('diff_view.settings.open', 'DIFF_VIEW_SETTINGS_BUTTON')
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          <button
            className={clsx(
              'Button Button--iconOnly',
              invisible ? 'Button--invisible' : 'Button--secondary',
              small && 'Button--small',
            )}
            aria-label="Open diff view settings"
            onClick={handleDiffSettingsClick}
          >
            <GearIcon />
          </button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            <DiffViewPreferenceToggle reloadOnChange={reloadOnSplitPreferenceChange} />
            <DiffCommentsPreferenceToggle />
            <ActionList.Divider />
            <DiffLinePresentationToggles
              defaultHideWhitespace={defaultHideWhitespace}
              lineSpacingPreferenceAvailable={lineSpacingPreferenceAvailable}
              onUpdateWhitespace={onUpdateWhitespace}
              reloadOnChange={reloadOnWhitespaceChange ? () => window.location.reload() : undefined}
            />
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <DiffCompactLinesPopover />
    </>
  )
}

try{ DiffViewSettings.displayName ||= 'DiffViewSettings' } catch {}